import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { handleApiError } from '../../api/errorMapping';
import { useAuth } from '../../hooks/useAuth';
import { shortId } from '../../lib/shortId';
import { isUuid } from '../../lib/displayName';
import { Gate } from '../permissions/Gate';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { StatusBadge } from './StatusBadge';
import { Button } from '../ui/button';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { ManageUserRolesDialog } from '../tenant/ManageUserRolesDialog';
import { EditUserDialog } from './EditUserDialog';
import { useTenantUserFields } from '../../hooks/useTenantUserFields';
import type {
    TenantUserListItemLocal,
    TenantUserDetailLocal,
    UpdateTenantUserRequestLocal,
} from '../../api/types/TenantLocal';
import type { Permission, Scope } from '../../api/types/permissions';

/**
 * Funciones de API que difieren por contexto (tenant / platform drill-down). Cada flujo inyecta su
 * binding (`tenantApi.*` / `platformApi.*TenantUser`) ya cerrado sobre los ids correctos, de modo que
 * el menú no sabe nada de tenantId concretos.
 */
export interface UserActionsApi {
    getDetail: () => Promise<TenantUserDetailLocal>;
    update: (body: UpdateTenantUserRequestLocal) => Promise<unknown>;
    disable: () => Promise<unknown>;
    enable: () => Promise<unknown>;
    delete: () => Promise<unknown>;
    resetPassword: () => Promise<{ temporaryPassword: string }>;
    resetMfa: () => Promise<unknown>;
    resendInvitation: () => Promise<unknown>;
}

/**
 * Permisos del flujo. El prefijo del recurso `user.*` es `tenant.user.*`; la asignación de rol SIEMPRE
 * es `tenant.role.assign` (el backend re-enforza el scope).
 */
export interface UserActionsPerms {
    update: Permission;
    passwordReset: Permission;
    mfaReset: Permission;
    status: Permission;
    delete: Permission;
}

interface Props {
    user: TenantUserListItemLocal;
    /** Tenant donde viven las asignaciones de rol (drill-down platform / actor en retailer / tenant propio). */
    tenantId: string;
    /** Scope/scopeRef con que se gatean los items en la UI. El backend re-enforza. */
    gateScope: Scope;
    gateScopeRef: string;
    api: UserActionsApi;
    perms: UserActionsPerms;
    /** queryKey del DETALLE del usuario (para el dialog de roles y el de detalle). */
    detailQueryKey: QueryKey;
    /** queryKey del LISTADO de usuarios (para invalidar tras una acción). */
    listQueryKey: QueryKey;
}

/**
 * Menú "⋮" de acciones por fila de usuario, COMPARTIDO por los tres flujos de usuarios de tenant
 * (tenant propio, retailer, drill-down platform). Garantiza un set idéntico de acciones, en el mismo
 * orden y con los mismos labels; lo único que cambia por contexto son el binding de API, los ids/scopes
 * y el prefijo de permisos, que se inyectan por props.
 *
 * Orden: Ver detalle · Gestionar roles · Editar · Reenviar invitación (INVITED) · Restablecer contraseña
 * (ACTIVE) · Restablecer MFA (ACTIVE) · Deshabilitar/Habilitar · Eliminar.
 *
 * Cada acción de escritura está gateada por su permiso (el backend re-enforza) y condicionada por el
 * estado del usuario. Las destructivas piden confirmación. `modal={false}` evita el page-lock de Radix
 * (pointer-events:none en body) cuando la fila se desmonta tras confirmar una acción.
 */
export function UserActionsMenu({
    user,
    tenantId,
    gateScope,
    gateScopeRef,
    api,
    perms,
    detailQueryKey,
    listQueryKey,
}: Props) {
    const qc = useQueryClient();
    const { hasPermission, hasScopeAccess } = useAuth();
    const sub = user.cognitoSub;
    const status = user.status;
    const [confirm, setConfirm] = useState<null | 'disable' | 'delete'>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [manageRolesOpen, setManageRolesOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [tempPass, setTempPass] = useState<string | null>(null);

    // Detalle enriquecido (roleAssignments con scope/scopeRef + nombre legible del destino). Solo al abrir.
    const { data: detail } = useQuery({
        queryKey: detailQueryKey,
        queryFn: api.getDetail,
        enabled: detailOpen,
    });

    // Catálogo de campos personalizados (enabled) del tenant, para mostrarlos en "Ver detalle".
    const { data: customFields = [] } = useTenantUserFields(tenantId, { enabledOnly: true });
    // Orden de presentación: alfabético por label (default del proyecto).
    const sortedCustomFields = [...customFields].sort((a, b) => a.label.localeCompare(b.label));

    // Resolver de nombre de destino para el detalle: si el backend no pobló `scopeRefName`, evitamos
    // mostrar el UUID crudo del scopeRef → caemos a un id acortado — NUNCA al UUID pelado.
    // DEC-013: NO confiamos ciegamente en `scopeRefName` — el backend lo pobla con el scopeRef crudo
    // (UUID) cuando no tiene el nombre real, así que un `scopeRefName` con forma de UUID se descarta.
    const resolveScopeRefName = (scopeRef: string, scopeRefName?: string): string => {
        if (scopeRefName && !isUuid(scopeRefName)) return scopeRefName;
        return shortId(scopeRef);
    };

    const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: listQueryKey });

    const disableMut = useMutation({
        mutationFn: () => api.disable(),
        onSuccess: async () => {
            toast.success('Usuario deshabilitado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const enableMut = useMutation({
        mutationFn: () => api.enable(),
        onSuccess: async () => {
            toast.success('Usuario habilitado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const deleteMut = useMutation({
        mutationFn: () => api.delete(),
        onSuccess: async () => {
            toast.success('Usuario eliminado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const resetMfaMut = useMutation({
        mutationFn: () => api.resetMfa(),
        onSuccess: () => {
            toast.success('MFA restablecido');
        },
        onError: handleApiError,
    });
    const resendMut = useMutation({
        mutationFn: () => api.resendInvitation(),
        onSuccess: () => {
            toast.success('Invitación reenviada');
        },
        onError: handleApiError,
    });
    const resetPwMut = useMutation({
        mutationFn: () => api.resetPassword(),
        onSuccess: (r) => {
            setTempPass(r.temporaryPassword);
        },
        onError: handleApiError,
    });
    const roleNames = detail?.roles ?? user.roles ?? [];

    // Sin scope o sin ninguna acción habilitada no mostramos el "⋮" (cada item igual se gatea con
    // <Gate>; esto solo evita un menú vacío). Va DESPUÉS de todos los hooks.
    const actionPerms: Permission[] = [
        'tenant.role.assign',
        perms.update,
        perms.passwordReset,
        perms.mfaReset,
        perms.status,
        perms.delete,
    ];
    if (!hasScopeAccess(gateScope, gateScopeRef) || !actionPerms.some((p) => hasPermission(p))) {
        return null;
    }

    return (
        <>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Acciones">
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                        onClick={() => {
                            setDetailOpen(true);
                        }}
                    >
                        Ver detalle
                    </DropdownMenuItem>
                    <Gate permission="tenant.role.assign" scope={gateScope} scopeRef={gateScopeRef}>
                        <DropdownMenuItem
                            onClick={() => {
                                setManageRolesOpen(true);
                            }}
                        >
                            Gestionar roles
                        </DropdownMenuItem>
                    </Gate>
                    <Gate permission={perms.update} scope={gateScope} scopeRef={gateScopeRef}>
                        <DropdownMenuItem
                            onClick={() => {
                                setEditOpen(true);
                            }}
                        >
                            Editar
                        </DropdownMenuItem>
                    </Gate>
                    {status === 'INVITED' && (
                        <Gate
                            permission={perms.passwordReset}
                            scope={gateScope}
                            scopeRef={gateScopeRef}
                        >
                            <DropdownMenuItem
                                onClick={() => {
                                    resendMut.mutate();
                                }}
                            >
                                Reenviar invitación
                                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper solo frena la propagación al DropdownMenuItem; el control interactivo real es el botón del HelpHint */}
                                <span
                                    className="ml-auto"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <HelpHint text={HELP_TEXT.activationLink} side="right" />
                                </span>
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {status === 'ACTIVE' && (
                        <Gate
                            permission={perms.passwordReset}
                            scope={gateScope}
                            scopeRef={gateScopeRef}
                        >
                            <DropdownMenuItem
                                onClick={() => {
                                    resetPwMut.mutate();
                                }}
                            >
                                Restablecer contraseña
                                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper solo frena la propagación al DropdownMenuItem; el control interactivo real es el botón del HelpHint */}
                                <span
                                    className="ml-auto"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <HelpHint text={HELP_TEXT.passwordReset} side="right" />
                                </span>
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {status === 'ACTIVE' && (
                        <Gate permission={perms.mfaReset} scope={gateScope} scopeRef={gateScopeRef}>
                            <DropdownMenuItem
                                onClick={() => {
                                    resetMfaMut.mutate();
                                }}
                            >
                                Restablecer MFA
                                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper solo frena la propagación al DropdownMenuItem; el control interactivo real es el botón del HelpHint */}
                                <span
                                    className="ml-auto"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <HelpHint text={HELP_TEXT.mfaReset} side="right" />
                                </span>
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    <DropdownMenuSeparator />
                    {status === 'ACTIVE' && (
                        <Gate permission={perms.status} scope={gateScope} scopeRef={gateScopeRef}>
                            <DropdownMenuItem
                                onClick={() => {
                                    setConfirm('disable');
                                }}
                            >
                                Deshabilitar
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {status === 'SUSPENDED' && (
                        <Gate permission={perms.status} scope={gateScope} scopeRef={gateScopeRef}>
                            <DropdownMenuItem
                                onClick={() => {
                                    enableMut.mutate();
                                }}
                            >
                                Habilitar
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    <Gate permission={perms.delete} scope={gateScope} scopeRef={gateScopeRef}>
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                                setConfirm('delete');
                            }}
                        >
                            Eliminar
                        </DropdownMenuItem>
                    </Gate>
                </DropdownMenuContent>
            </DropdownMenu>

            <ManageUserRolesDialog
                open={manageRolesOpen}
                onOpenChange={setManageRolesOpen}
                tenantId={tenantId}
                cognitoSub={sub}
                userLabel={user.email}
                fetchDetail={api.getDetail}
                detailQueryKey={detailQueryKey}
                listQueryKey={listQueryKey}
                gateScope={gateScope}
                gateScopeRef={gateScopeRef}
            />

            <ConfirmDialog
                open={confirm === 'disable'}
                onOpenChange={(o) => {
                    if (!o) setConfirm(null);
                }}
                title="Deshabilitar usuario"
                description={`${user.email} no podrá iniciar sesión hasta que lo habilites de nuevo. ¿Continuar?`}
                confirmLabel="Deshabilitar"
                destructive
                onConfirm={() => {
                    disableMut.mutate();
                }}
            />
            <ConfirmDialog
                open={confirm === 'delete'}
                onOpenChange={(o) => {
                    if (!o) setConfirm(null);
                }}
                title="Eliminar usuario"
                description={`Se eliminará a ${user.email}. Esta acción no se puede deshacer. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                loading={deleteMut.isPending}
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />

            {/* Ver detalle: datos del usuario + roles y alcance (resueltos por el GET single). */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Detalle del usuario</DialogTitle>
                        <DialogDescription>{user.email}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1 text-sm">
                            <div>
                                <span className="text-muted-foreground">Nombre: </span>
                                {user.displayName}
                            </div>
                            <div>
                                <span className="text-muted-foreground">Email: </span>
                                {user.email}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Estado:</span>
                                <StatusBadge status={status} />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <p className="flex items-center gap-1 text-sm font-medium">
                                Roles y alcance
                                <HelpHint text={HELP_TEXT.roleAssignment} />
                            </p>
                            {(detail?.roleAssignments ?? []).length > 0 ? (
                                <ul className="space-y-1">
                                    {detail?.roleAssignments?.map((a) => (
                                        <li
                                            key={`${a.roleId}-${a.scope}-${a.scopeRef}`}
                                            className="flex flex-wrap items-center gap-2 text-sm"
                                        >
                                            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                                                {a.roleName}
                                            </span>
                                            <span className="text-muted-foreground">
                                                {a.scope}
                                                {a.scope !== 'PLATFORM' && a.scope !== 'TENANT'
                                                    ? `: ${resolveScopeRefName(a.scopeRef, a.scopeRefName)}`
                                                    : ''}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : roleNames.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Sin roles</p>
                            ) : (
                                <ul className="flex flex-wrap gap-1">
                                    {roleNames.map((r) => (
                                        <li
                                            key={r}
                                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                                        >
                                            {r}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Campos personalizados del tenant (solo lectura). Muestra todos los enabled
                            con su valor o "—" si vacío. Orden alfabético por label. */}
                        {sortedCustomFields.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Campos personalizados</p>
                                <dl className="space-y-1 text-sm">
                                    {sortedCustomFields.map((f) => (
                                        <div key={f.key} className="flex gap-2">
                                            <dt className="text-muted-foreground">{f.label}:</dt>
                                            <dd>{detail?.customFields?.[f.key] || '—'}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDetailOpen(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Editar nombre/teléfono + campos personalizados del tenant (form propio con RHF). */}
            <EditUserDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                userEmail={user.email}
                userDisplayName={user.displayName}
                tenantId={tenantId}
                getDetail={api.getDetail}
                detailQueryKey={detailQueryKey}
                update={api.update}
                onSaved={invalidate}
            />

            {/* Contraseña temporal tras restablecer */}
            <Dialog
                open={tempPass !== null}
                onOpenChange={(o) => {
                    if (!o) setTempPass(null);
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Contraseña temporal</DialogTitle>
                        <DialogDescription>
                            Compartila con {user.email}. Deberá cambiarla al iniciar sesión.
                        </DialogDescription>
                    </DialogHeader>
                    <code className="block select-all rounded bg-muted p-3 text-center font-mono text-lg">
                        {tempPass}
                    </code>
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setTempPass(null);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
